import { PageSurface } from "./PageBody";
import { styled } from "goober";
import { type ReactNode, useLayoutEffect, useRef } from "react";
import { useScrollProgress } from "../hooks/useScrollProgress";
import { useScrollRestoration } from "../hooks/useScrollRestoration";

const HEADER_OVERLAP = 16;

const Outer = styled("div")`
  flex: 1;
  /* min-width:0 lets this shrink inside Backdrop's flex row so a wide list table scrolls
     within the panel instead of stretching the hero/composer past the viewport. */
  min-width: 0;
  display: flex;
  flex-direction: column;
  min-height: 0;
`;

const Scroll = styled(PageSurface)`
  flex: 1;
  min-height: 0;
  overflow: auto;
  overscroll-behavior: none;
  background: var(--surface-primary);
  border-radius: 16px;
  outline: 0.5px solid var(--border-alpha);
  box-shadow: var(--elevation-low);
  display: flex;
  flex-direction: column;
`;

const HeroSlot = styled("div")`
  position: sticky;
  left: 0;
  flex-shrink: 0;
  overflow: hidden;
`;

const ListArea = styled("div")`
  flex-shrink: 0;
  min-width: min-content;
  min-height: calc(100% - var(--sticky-top, 0px));
  display: flex;
  flex-direction: column;
`;

const StickyTabHeader = styled("div")`
  position: sticky;
  top: 0;
  left: 0;
  z-index: 4;
  flex-shrink: 0;
  background: var(--surface-primary);
  border-radius: 15px 15px 0 0;
  box-shadow:
    var(--elevation-low-up),
    0 -0.5px 0 0 var(--border-alpha);
  clip-path: inset(-20px 0 0 0);

  &[data-has-hero="true"] {
    margin-top: -${HEADER_OVERLAP}px;
  }
`;

type Props = {
  ready: boolean;
  hero?: ReactNode;
  heroHeight?: number;
  tabHeader: ReactNode;
  children: ReactNode;
};

export function DismissibleHeroLayout({
  ready,
  hero,
  heroHeight = 240,
  tabHeader,
  children,
}: Props) {
  const scrollRef = useScrollRestoration<HTMLDivElement>({ ready });
  const tabHeaderEl = useRef<HTMLDivElement | null>(null);

  const range = hero ? heroHeight : 0;
  useScrollProgress({ scrollRef, targetRef: scrollRef, range });

  useLayoutEffect(() => {
    const scroll = scrollRef.current;
    const tabHead = tabHeaderEl.current;
    if (!scroll || !tabHead) return;
    const update = () => {
      const h = Math.floor(tabHead.getBoundingClientRect().height);
      scroll.style.setProperty("--sticky-top", `${h}px`);
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(tabHead);
    return () => ro.disconnect();
  }, [scrollRef]);

  useLayoutEffect(() => {
    const scroll = scrollRef.current;
    if (!scroll) return;
    const MAX = 150;
    let target = 0;
    let displayed = 0;
    let raf: number | null = null;
    const apply = () => {
      const normalized = Math.max(0, Math.min(1, displayed / MAX));
      scroll.style.setProperty("--overscroll", normalized.toFixed(4));
    };
    const tick = () => {
      raf = null;
      target *= 0.88;
      displayed += (target - displayed) * 0.18;
      if (target < 0.5) target = 0;
      if (Math.abs(displayed) < 0.5) displayed = 0;
      apply();
      if (target > 0 || displayed > 0) raf = requestAnimationFrame(tick);
    };
    const onWheel = (e: WheelEvent) => {
      if (scroll.scrollTop === 0 && e.deltaY < 0) {
        const resistance = Math.max(0, 1 - target / MAX);
        target = Math.min(MAX, target + Math.abs(e.deltaY) * 0.4 * resistance);
      } else if (target > 0) {
        target = 0;
      }
      if (!raf) raf = requestAnimationFrame(tick);
    };
    scroll.addEventListener("wheel", onWheel, { passive: true });
    return () => {
      scroll.removeEventListener("wheel", onWheel);
      if (raf) cancelAnimationFrame(raf);
    };
  }, [scrollRef]);

  return (
    <Outer>
      <Scroll
        ref={scrollRef}
        style={{
          ["--hero-height" as string]: hero
            ? `${heroHeight + HEADER_OVERLAP}px`
            : "0px",
        }}
      >
        {hero ? (
          <HeroSlot style={{ height: heroHeight + HEADER_OVERLAP }}>
            {hero}
          </HeroSlot>
        ) : null}
        <StickyTabHeader
          ref={tabHeaderEl}
          data-has-hero={hero ? "true" : "false"}
        >
          {tabHeader}
        </StickyTabHeader>
        <ListArea>{children}</ListArea>
      </Scroll>
    </Outer>
  );
}
